import isEmpty from 'lodash/isEmpty'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'

import documents from 'modules/documents/store/collections/documents'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import { CustomFieldAttributes } from 'modules/timeoff/lib/DocumentForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import leaves from 'modules/timeoff/store/collections/leaves'
import type Leave from 'modules/timeoff/store/models/Leave'

interface Props {
  form: LeaveForm
  documentForm: DocumentForm
  onLeaveSuccess: ((leave: Leave) => Promise<unknown>) | null | undefined
  onLeaveError: () => void
  getFieldsValues: () => CustomFieldAttributes[]
  fetchStats: (leave: Leave, employeeId: number) => void
  onCloseYear: () => void
  leaveEmployeeId: number
  sickFolderId: number
}
interface ReturnType {
  onSubmit: () => void
  isProcessing: boolean
}

const useCreateLeave = ({
  form,
  documentForm,
  getFieldsValues,
  onLeaveSuccess,
  onLeaveError,
  fetchStats,
  onCloseYear,
  leaveEmployeeId,
  sickFolderId,
}: Props): ReturnType => {
  const { fetchData } = useLeaves()

  const saveDocument = async (leave: Leave) => {
    if (isEmpty(documentForm.get('file').value)) return

    const customFieldAttributes = getFieldsValues()
    documentForm.setDocumentValues(leave, customFieldAttributes, leaveEmployeeId, sickFolderId)
    await documentForm.create(documents)
  }

  const createLeave = async () => {
    const payload = {
      ...form.data(),
      hours_amount_in_cents: form.hoursAmount(),
    }

    const result = await asyncAction(() => leaves.create(payload), {
      successFlash: false,
    })

    if (isSuccessful(result)) {
      const { value } = result

      const leave = leaves.find({
        id: value.id,
      })

      if (!leave) {
        throw Error(`Error finding leaves with id = ${value.id}`)
      }

      // Medical leaves manage documents by them self
      if (onLeaveSuccess) {
        await onLeaveSuccess(leave)
      } else {
        await saveDocument(leave)
      }

      dispatch({
        type: EventTypes.LEAVE_CREATED,
        data: { leave: leave },
      })
      fetchStats(leave, leaveEmployeeId)
      fetchData(leaveEmployeeId)
      onCloseYear()
    } else {
      onLeaveError()
    }
  }

  const { running: isProcessing, performAction: onSubmit } = useAsyncAction(() => createLeave(), {
    successFlash: false,
  })

  return {
    onSubmit,
    isProcessing,
  }
}

export default useCreateLeave
